import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { makeRequest } from "../../../../assets/functions_helper";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../../components/customDialog";
import SortableList, { SortableItem } from "react-easy-sort";
import { CloseButton } from "../../../../components/buttons";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../../../components/selectFilterStyled"; // 👈 reutilizamos tu select

function ProductSelectionDialog({
  open,
  handleClose,
  restaurantId,
  onAddProduct,
}) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🔄 Obtener productos activos
  useEffect(() => {
    const fetchProducts = () => {
      setLoading(true);
      makeRequest(
        "GET",
        `menu_manager/list_restaurant_products?restaurant_id=${restaurantId}&page=1&size=200`,
        {},
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => {
          navigate("/login");
        }
      )
        .then((response) => {
          const activeProducts = response.products.filter(
            (product) => product.is_active
          );

          // Extraer categorías únicas
          const uniqueCategories = [
            ...new Map(
              activeProducts.map((p) => [
                p.category,
                { id: p.category, title: p.category },
              ])
            ).values(),
          ];

          setProducts(activeProducts);
          setFilteredProducts(activeProducts);
          setCategories(uniqueCategories);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error al obtener los productos:", error);
          setLoading(false);
        });
    };

    if (open) {
      fetchProducts();
      setSearchTerm("");
      setSelectedCategory("");
    }
  }, [open, restaurantId, navigate]);

  // 🧠 Filtrado por categoría + nombre
  useEffect(() => {
    let filtered = [...products];

    if (selectedCategory) {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchTerm.trim() !== "") {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(lowerTerm)
      );
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm]);

  const handleProductClick = (product) => {
    onAddProduct(product);
    handleClose();
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const onSortEnd = () => { };

  return (
    <CustomDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <CustomDialogTitle>Selecciona un Producto para Agregar</CustomDialogTitle>

      <CustomDialogContent>
        {loading ? (
          <Box sx={{ height: "40vh" }} />
        ) : (
          <>
            {/* 🔍 Filtros arriba */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "15px",
                marginBottom: "25px",
              }}
            >
              {/* Filtro por categoría */}
              <CustomSelect
                value={selectedCategory}
                onChange={handleCategoryChange}
                valuesFilter={categories}
              />

              {/* Filtro por nombre con ícono 🔍 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(255,255,255,0.08)",
                  boxShadow: "0px 0px 6px rgba(255,255,255,0.05) inset",
                  border: "0.5px solid rgba(255,255,255,0.5)",
                  borderRadius: "200px",
                  px: 2,
                  height: 42,
                  width: 200,
                }}
              >
                <span
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    marginRight: 8,
                    fontSize: "16px",
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Buscar producto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "black",
                    width: "100%",
                    fontSize: "14px",
                  }}
                />
              </Box>
            </Box>

            {/* 🧱 Lista de productos */}
            {filteredProducts.length === 0 ? (
              <Typography
                sx={{
                  color: "white",
                  textAlign: "center",
                  marginTop: "20px",
                  opacity: 0.7,
                }}
              >
                No hay productos que coincidan con los filtros.
              </Typography>
            ) : (
              <SortableList
                className="list"
                onSortEnd={onSortEnd}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: "20px",
                }}
              >
                {filteredProducts.map((product, index) => (
                  <SortableItem key={index}>
                    <Box
                      onClick={() => handleProductClick(product)}
                      sx={{
                        width: 130,
                        height: 130,
                        borderRadius: "10px",
                        backgroundImage: `url(${product.image_url || "https://via.placeholder.com/150"
                          })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        cursor: "pointer",
                        overflow: "hidden",
                        boxShadow: "0 0 10px rgba(255,255,255,0.1)",
                        "&:hover": {
                          transform: "scale(1.05)",
                          transition: "all 0.3s ease",
                          boxShadow: "0 0 15px rgba(255,255,255,0.3)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.6)",
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "white",
                            textAlign: "center",
                            fontWeight: 500,
                            textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
                            px: 1,
                          }}
                        >
                          {product.name}
                          <br />
                          <Typography
                            component="span"
                            sx={{ fontSize: 12, opacity: 0.8 }}
                          >
                            Normal: ${product.price}
                          </Typography>
                          <br />
                          {product.discounted_price && <Typography
                            component="span"
                            sx={{ fontSize: 12, opacity: 0.8 }}
                          >
                            Descuento: ${product.discounted_price}
                          </Typography>}
                        </Typography>
                      </Box>
                    </Box>
                  </SortableItem>
                ))}
              </SortableList>
            )}
          </>
        )}
      </CustomDialogContent>

      <CustomDialogActions>
        <CloseButton onClick={handleClose}>Cerrar</CloseButton>
      </CustomDialogActions>
    </CustomDialog>
  );
}

export default ProductSelectionDialog;
